import { Select, Space, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductsSearchProducts } from '@/api/generated/endpoints/products/products';
import type { ProductRef } from '@/api/generated/model';
import { uomLabel } from '@/lib/units';
import { useOutbox } from '@/offline/context';
import { searchCached } from '@/offline/productCache';

const MIN_QUERY = 2; // как SEARCH_MIN_LENGTH на бэкенде
const DEBOUNCE_MS = 250;

export interface ProductSelectProps {
  value?: number | null | undefined;
  onChange?: (productId: number | null, product: ProductRef | null) => void;
  /** Уже выбранный товар — чтобы показать его название без поиска (форма редактирования). */
  initial?: ProductRef | null | undefined;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  /** Доступное имя, когда у поля нет своей подписи (<label>) рядом. */
  ariaLabel?: string;
}

/**
 * Выбор товара с поиском по названию и алиасам (с опечатками, pg_trgm).
 * Рядом с названием — единица учёта: кладовщик сразу видит, в кг товар или в шт.
 */
export function ProductSelect({
  value,
  onChange,
  initial,
  placeholder,
  disabled = false,
  id,
  ariaLabel,
}: ProductSelectProps) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<ProductRef | null>(initial ?? null);

  useEffect(() => {
    const t = setTimeout(() => {
      setQuery(text.trim());
    }, DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
    };
  }, [text]);

  const search = useProductsSearchProducts(
    { q: query, limit: 20 },
    { query: { enabled: query.length >= MIN_QUERY, staleTime: 10_000 } },
  );
  const { online } = useOutbox();
  // Без связи — поиск по сохранённой копии справочника (T-184).
  const offline = !online || search.isError;
  const hits =
    offline && query.length >= MIN_QUERY
      ? searchCached(query).map((product) => ({ product, alias: null }))
      : (search.data?.map((h) => ({ product: h.product, alias: h.matched_alias })) ?? []);
  const current = picked && picked.id === value ? picked : (initial ?? null);
  const options = [
    ...(current && !hits.some((h) => h.product.id === current.id)
      ? [{ product: current, alias: null }]
      : []),
    ...hits,
  ];

  return (
    <Select<number>
      {...(id === undefined ? {} : { id })}
      {...(ariaLabel === undefined ? {} : { 'aria-label': ariaLabel })}
      showSearch
      allowClear
      filterOption={false}
      value={value ?? null}
      placeholder={placeholder ?? t('productSelect.placeholder')}
      disabled={disabled}
      loading={search.isFetching}
      onSearch={setText}
      notFoundContent={
        query.length < MIN_QUERY
          ? t('productSelect.minChars', { count: MIN_QUERY })
          : t('productSelect.nothingFound')
      }
      onChange={(id: number | undefined) => {
        const product = options.find((o) => o.product.id === id)?.product ?? null;
        setPicked(product);
        onChange?.(id ?? null, product);
      }}
      options={options.map(({ product, alias }) => ({
        value: product.id,
        label: (
          <Space size={4}>
            <span>{product.name}</span>
            <Tag>{uomLabel(t, product.base_uom)}</Tag>
            {alias && (
              <Typography.Text type="secondary" ellipsis>
                ← {alias}
              </Typography.Text>
            )}
          </Space>
        ),
      }))}
    />
  );
}
